import type { TrackingCore } from "./core";

type KikiType = "event_tied" | "standalone";
type KikiCreateMode = "event_tied" | "standalone";
type KikiViewerStatus = "owner" | "accepted" | "pending" | "declined" | "visitor";
type KikiApplicantDecision = "accepted" | "declined" | "undo";
type KikiChatType = "group" | "application";

/**
 * Discovery surface events: the public events index (the carousel/filter feed at
 * /e) and the Kiki community feature (browse carousel, creation funnel, detail
 * page). Kept funnel-focused — high-signal intent moments, not per-field noise.
 * Merged onto the tracking singleton in index.ts; everything flows through
 * this.track() so it reaches PostHog + GTM + native FB like the rest.
 */
export const discoveryEvents = {
  // ─── Events index (discovery feed) ────────────────────────────────────────
  eventsIndexViewed(this: TrackingCore, signedIn: boolean) {
    this.track("events_index_viewed", {
      signed_in: signedIn,
    });
  },

  // One event for every filter interaction. `filter` is the dimension touched,
  // `value` its new value (label/id, or null when a chip is cleared), `active`
  // whether the filter is now applied (false = removed/toggled off).
  eventsFilterChanged(
    this: TrackingCore,
    filter: "type" | "date" | "city" | "category",
    value: string | null,
    active: boolean,
  ) {
    this.track("events_filter_changed", {
      filter,
      value,
      active,
    });
  },

  eventsCardClicked(
    this: TrackingCore,
    eventId: string,
    opts: { isOutclose: boolean; carouselType?: string; position?: number },
  ) {
    this.track("events_card_clicked", {
      event_id: eventId,
      is_outclose: opts.isOutclose,
      carousel_type: opts.carouselType,
      position: opts.position,
    });
  },

  eventsCarouselExpanded(this: TrackingCore, sectionType: "list" | "grid") {
    this.track("events_carousel_expanded", {
      section_type: sectionType,
    });
  },

  // EventCard "interested" heart toggle (also surfaces on the index feed).
  eventInterestedToggled(this: TrackingCore, eventId: string, isInterested: boolean) {
    this.track("event_interested_toggled", {
      event_id: eventId,
      is_interested: isInterested,
    });
  },

  eventsAddEventClicked(this: TrackingCore) {
    this.track("events_add_event_clicked");
  },

  // Beta notice shown to signed-in users on the events index.
  eventsBetaBannerShown(this: TrackingCore) {
    this.track("events_beta_banner_shown");
  },

  eventsBetaBannerClicked(
    this: TrackingCore,
    action: "feedback" | "how_it_works" | "dismiss",
  ) {
    this.track("events_beta_banner_clicked", { action });
  },

  // ─── Kiki discovery ───────────────────────────────────────────────────────
  kikiHelpClicked(this: TrackingCore, source: "carousel" | "detail") {
    this.track("kiki_help_clicked", { source });
  },

  kikiCarouselCardClicked(this: TrackingCore, kikiId: string, kikiType: KikiType) {
    this.track("kiki_carousel_card_clicked", {
      kiki_id: kikiId,
      kiki_type: kikiType,
    });
  },

  // Sign-in gate hit from a Kiki surface. `source` distinguishes which prompt.
  kikiSignInClicked(
    this: TrackingCore,
    source: "no_kikis" | "browse_overlay" | "detail",
  ) {
    this.track("kiki_signin_clicked", { source });
  },

  // ─── Kiki creation funnel ─────────────────────────────────────────────────
  // initiated (template/category picked) → modal_opened → submitted | error.
  kikiCreateInitiated(
    this: TrackingCore,
    categoryId: string,
    source: "carousel" | "detail" | "event",
  ) {
    this.track("kiki_create_initiated", {
      category_id: categoryId,
      source,
    });
  },

  kikiCreateModalOpened(this: TrackingCore, mode: KikiCreateMode) {
    this.track("kiki_create_modal_opened", { mode });
  },

  kikiCreateSubmitted(this: TrackingCore, mode: KikiCreateMode, hasNote: boolean) {
    this.track("kiki_create_submitted", {
      mode,
      has_note: hasNote,
    });
  },

  kikiCreateError(this: TrackingCore, mode: KikiCreateMode, error: string) {
    this.track("kiki_create_error", {
      mode,
      error_message: error,
    });
  },

  kikiUpdated(this: TrackingCore, kikiId: string) {
    this.track("kiki_updated", { kiki_id: kikiId });
  },

  kikiSubscribeRequiredShown(this: TrackingCore) {
    this.track("kiki_subscribe_required_shown");
  },

  // ─── Kiki detail page ─────────────────────────────────────────────────────
  kikiPageViewed(this: TrackingCore, kikiId: string, viewerStatus: KikiViewerStatus) {
    this.track("kiki_page_viewed", {
      kiki_id: kikiId,
      viewer_status: viewerStatus,
    });
  },

  kikiStatusToggled(this: TrackingCore, kikiId: string, newStatus: string) {
    this.track("kiki_status_toggled", {
      kiki_id: kikiId,
      new_status: newStatus,
    });
  },

  kikiDeleted(this: TrackingCore, kikiId: string) {
    this.track("kiki_deleted", { kiki_id: kikiId });
  },

  kikiApplicantDecided(
    this: TrackingCore,
    kikiId: string,
    decision: KikiApplicantDecision,
  ) {
    this.track("kiki_applicant_decided", {
      kiki_id: kikiId,
      decision,
    });
  },

  kikiChatOpened(this: TrackingCore, kikiId: string, chatType: KikiChatType) {
    this.track("kiki_chat_opened", {
      kiki_id: kikiId,
      chat_type: chatType,
    });
  },

  kikiReachOutSubmitted(this: TrackingCore, kikiId: string, hasNote: boolean) {
    this.track("kiki_reach_out_submitted", {
      kiki_id: kikiId,
      has_note: hasNote,
    });
  },
};
